import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../services/contexts/AuthContext';
import { 
  Home,
  UserCheck, 
  MessageCircle, 
  Activity, 
  Send,
  Stethoscope,
  Users,
  FileText,
  Clock,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Zap,
  PlayCircle,
  Monitor
} from 'lucide-react';

const TenantNurseSidebar = ({ activeView, setActiveView }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Nursing Dashboard',
      icon: Home,
      color: 'text-blue-600',
      path: '/tenant-nurse/dashboard'
    },
    {
      id: 'patients',
      label: 'Patient Care',
      icon: UserCheck,
      color: 'text-green-600',
      path: '/tenant-nurse/patients'
    },
    {
      id: 'recordings',
      label: 'Recording Management',
      icon: Monitor,
      color: 'text-va-primary-600',
      path: '/tenant-nurse/recordings'
    },
    {
      id: 'communications',
      label: 'Patient Communications',
      icon: Send,
      color: 'text-orange-600',
      path: '/tenant-nurse/communications'
    }
  ];

  const handleNavigation = (item) => {
    setActiveView(item.id);
    navigate(item.path);
  };

  const getCurrentPath = () => {
    return location.pathname.split('/').pop();
  };

  return (
    <div className="w-80 bg-white shadow-lg h-screen overflow-y-auto flex flex-col border-r border-gray-200">
      {/* Logo and Brand */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <Stethoscope className="h-7 w-7 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white">
              <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
            </div>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">VitalAudio</h1>
            <p className="text-sm text-gray-500 font-medium">Nursing Care</p>
          </div>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="p-4">
        <div className="bg-gradient-to-r from-green-50 to-teal-50 p-4 rounded-xl border border-green-100">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">
                {user?.name?.split(' ').map(n => n[0]).join('') || 'RN'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {user?.name || 'Registered Nurse'}
              </p>
              <p className="text-xs text-gray-600">Patient Care Coordinator</p>
              <div className="flex items-center mt-1">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-xs text-green-600 font-medium">On Duty</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-4 space-y-1">
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
            PATIENT CARE
          </p>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = getCurrentPath() === item.id || (getCurrentPath() === '' && item.id === 'dashboard');
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  isActive 
                    ? 'bg-green-50 text-green-700 border border-green-200 shadow-sm' 
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-green-600' : item.color} transition-colors`} />
                <span className="font-medium">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-2 h-2 bg-green-500 rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Patient Status Quick View */}
      <div className="p-4">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Patient Status</h3>
            <Activity className="h-4 w-4 text-blue-600" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Active Patients</span>
              <div className="flex items-center space-x-1">
                <span className="text-sm font-bold text-blue-600">24</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Pending Recordings</span>
              <div className="flex items-center space-x-1">
                <Clock className="h-3 w-3 text-amber-500" />
                <span className="text-sm font-bold text-amber-600">8</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Concerning Vitals</span>
              <div className="flex items-center space-x-1">
                <AlertCircle className="h-3 w-3 text-red-500" />
                <span className="text-sm font-bold text-red-600">2</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-4">
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Quick Actions</h3>
            <Zap className="h-4 w-4 text-purple-600" />
          </div>
          <div className="space-y-2">
            <button className="w-full flex items-center justify-between text-sm text-purple-700 hover:text-purple-900 transition-colors">
              <span>Send Recording Links</span>
              <Send className="h-3 w-3" />
            </button>
            <button className="w-full flex items-center justify-between text-sm text-purple-700 hover:text-purple-900 transition-colors">
              <span>Add Care Notes</span>
              <FileText className="h-3 w-3" />
            </button>
            <button className="w-full flex items-center justify-between text-sm text-purple-700 hover:text-purple-900 transition-colors">
              <span>Check Critical Alerts</span>
              <AlertCircle className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-100">
        <div className="text-center">
          <p className="text-xs text-gray-400">
            VitalAudio Nursing v1.0.0
          </p>
          <p className="text-xs text-gray-400">
            © 2025 Healthcare Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default TenantNurseSidebar;